import { useState, useEffect } from 'react';
import { useLogicModel, Outcome } from '../../../contexts/LogicModelContext';
import { supabase } from '../../../lib/supabase';
import { useTenant } from '../../../hooks/useTenant';

const TIMEFRAMES = [
  { value: 'short_term', label: 'Short-term (1-3 years)', description: 'Learning: awareness, knowledge, skills, attitudes' },
  { value: 'medium_term', label: 'Medium-term (4-6 years)', description: 'Action: behavior change, practice, decisions' },
  { value: 'long_term', label: 'Long-term (7-10 years)', description: 'Conditions: social, economic, environmental changes' },
];

const INDICATOR_STATUSES = [
  { value: 'not_started', label: 'Not Started', color: 'gray' },
  { value: 'in_progress', label: 'In Progress', color: 'yellow' },
  { value: 'achieved', label: 'Achieved', color: 'green' },
  { value: 'not_achieved', label: 'Not Achieved', color: 'red' },
];

export function OutcomesStep() {
  const { logicModel, updateOutcomes, markStepComplete } = useLogicModel();
  const { organization } = useTenant();
  const [outcomes, setOutcomes] = useState<Outcome[]>([]);
  const [activeTimeframe, setActiveTimeframe] = useState<string>('short_term');
  const [editingOutcome, setEditingOutcome] = useState<string | null>(null);
  const [editingIndicator, setEditingIndicator] = useState<{ outcomeId: string; indicatorId: string | null } | null>(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Form states
  const [outcomeForm, setOutcomeForm] = useState({ name: '', description: '' });
  const [indicatorForm, setIndicatorForm] = useState<{
    name: string;
    measurement_method: string;
    baseline_value: string;
    target_value: string;
    actual_value: string;
    target_date: string;
    data_source: string;
    status: 'not_started' | 'in_progress' | 'achieved' | 'not_achieved';
  }>({
    name: '',
    measurement_method: '',
    baseline_value: '',
    target_value: '',
    actual_value: '',
    target_date: '',
    data_source: '',
    status: 'not_started',
  });

  useEffect(() => {
    if (logicModel?.outcomes) {
      setOutcomes(logicModel.outcomes);
    }
  }, [logicModel?.outcomes]);

  useEffect(() => {
    if (outcomes.length > 0) {
      markStepComplete('outcomes');
    }
  }, [outcomes.length, markStepComplete]);

  const getOutcomesByTimeframe = (timeframe: string) =>
    outcomes.filter(o => o.timeframe === timeframe);

  const resetOutcomeForm = () => {
    setOutcomeForm({ name: '', description: '' });
    setEditingOutcome(null);
  };

  const resetIndicatorForm = () => {
    setIndicatorForm({
      name: '',
      measurement_method: '',
      baseline_value: '',
      target_value: '',
      actual_value: '',
      target_date: '',
      data_source: '',
      status: 'not_started',
    });
    setEditingIndicator(null);
  };

  // Outcome CRUD
  const handleSaveOutcome = async () => {
    if (!outcomeForm.name.trim() || !logicModel?.id || !organization) return;

    setSaving(true);
    setError(null);

    try {
      if (editingOutcome) {
        const { error: updateError } = await supabase
          .from('outcomes')
          .update({
            name: outcomeForm.name,
            description: outcomeForm.description || null,
          })
          .eq('id', editingOutcome);

        if (updateError) throw updateError;

        const updated = outcomes.map(o =>
          o.id === editingOutcome ? { ...o, ...outcomeForm } : o
        );
        setOutcomes(updated);
        updateOutcomes(updated);
      } else {
        const { data: newOutcome, error: insertError } = await supabase
          .from('outcomes')
          .insert({
            logic_model_id: logicModel.id,
            organization_id: organization.organization_id,
            timeframe: activeTimeframe,
            name: outcomeForm.name,
            description: outcomeForm.description || null,
            sort_order: getOutcomesByTimeframe(activeTimeframe).length,
          })
          .select()
          .single();

        if (insertError) throw insertError;

        const newOutcomes = [...outcomes, { ...newOutcome, indicators: [] }];
        setOutcomes(newOutcomes);
        updateOutcomes(newOutcomes);
      }
      resetOutcomeForm();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save outcome');
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteOutcome = async (outcomeId: string) => {
    if (!confirm('Delete this outcome and all its indicators?')) return;

    try {
      const { error: deleteError } = await supabase
        .from('outcomes')
        .delete()
        .eq('id', outcomeId);

      if (deleteError) throw deleteError;

      const updated = outcomes.filter(o => o.id !== outcomeId);
      setOutcomes(updated);
      updateOutcomes(updated);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to delete');
    }
  };

  // Indicator CRUD
  const handleSaveIndicator = async () => {
    if (!indicatorForm.name.trim() || !editingIndicator || !organization) return;

    setSaving(true);
    setError(null);

    try {
      const indicatorData = {
        name: indicatorForm.name,
        measurement_method: indicatorForm.measurement_method || null,
        baseline_value: indicatorForm.baseline_value ? parseFloat(indicatorForm.baseline_value) : null,
        target_value: indicatorForm.target_value ? parseFloat(indicatorForm.target_value) : null,
        actual_value: indicatorForm.actual_value ? parseFloat(indicatorForm.actual_value) : null,
        target_date: indicatorForm.target_date || null,
        data_source: indicatorForm.data_source || null,
        status: indicatorForm.status,
      };

      if (editingIndicator.indicatorId) {
        const { error: updateError } = await supabase
          .from('outcome_indicators')
          .update(indicatorData)
          .eq('id', editingIndicator.indicatorId);

        if (updateError) throw updateError;

        const updated = outcomes.map(o => {
          if (o.id !== editingIndicator.outcomeId) return o;
          return {
            ...o,
            indicators: o.indicators.map(i =>
              i.id === editingIndicator.indicatorId ? { ...i, ...indicatorData } : i
            ),
          };
        });
        setOutcomes(updated);
        updateOutcomes(updated);
      } else {
        const { data: newIndicator, error: insertError } = await supabase
          .from('outcome_indicators')
          .insert({
            outcome_id: editingIndicator.outcomeId,
            organization_id: organization.organization_id,
            ...indicatorData,
          })
          .select()
          .single();

        if (insertError) throw insertError;

        const updated = outcomes.map(o => {
          if (o.id !== editingIndicator.outcomeId) return o;
          return { ...o, indicators: [...o.indicators, newIndicator] };
        });
        setOutcomes(updated);
        updateOutcomes(updated);
      }
      resetIndicatorForm();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save indicator');
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteIndicator = async (outcomeId: string, indicatorId: string) => {
    if (!confirm('Delete this indicator?')) return;

    try {
      const { error: deleteError } = await supabase
        .from('outcome_indicators')
        .delete()
        .eq('id', indicatorId);

      if (deleteError) throw deleteError;

      const updated = outcomes.map(o => {
        if (o.id !== outcomeId) return o;
        return { ...o, indicators: o.indicators.filter(i => i.id !== indicatorId) };
      });
      setOutcomes(updated);
      updateOutcomes(updated);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to delete');
    }
  };

  const renderProgressBar = (baseline?: number | null, target?: number | null, actual?: number | null) => {
    if (baseline === null || baseline === undefined || target === null || target === undefined) return null;
    const progress = actual !== null && actual !== undefined
      ? Math.min(100, Math.max(0, ((actual - baseline) / (target - baseline)) * 100))
      : 0;

    return (
      <div className="mt-2">
        <div className="flex justify-between text-xs text-gray-500 mb-1">
          <span>Baseline: {baseline}</span>
          <span>Actual: {actual ?? '—'}</span>
          <span>Target: {target}</span>
        </div>
        <div className="w-full bg-gray-200 rounded-full h-2">
          <div
            className="bg-blue-600 h-2 rounded-full transition-all"
            style={{ width: `${progress}%` }}
          />
        </div>
      </div>
    );
  };

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-xl font-semibold text-gray-900 mb-2">
          📈 Define Outcomes & Indicators
        </h2>
        <p className="text-gray-600">
          What changes need to happen for participants to achieve your impact goal? Outcomes are grouped by timeframe and measured with SMART indicators.
        </p>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded">
          {error}
        </div>
      )}

      {/* Timeframe Tabs */}
      <div className="border-b border-gray-200">
        <nav className="flex -mb-px space-x-4">
          {TIMEFRAMES.map((tf) => (
            <button
              key={tf.value}
              onClick={() => setActiveTimeframe(tf.value)}
              className={`py-2 px-1 border-b-2 text-sm font-medium whitespace-nowrap ${
                activeTimeframe === tf.value
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              {tf.label}
              <span className="ml-2 px-2 py-0.5 rounded-full text-xs bg-gray-100">
                {getOutcomesByTimeframe(tf.value).length}
              </span>
            </button>
          ))}
        </nav>
      </div>

      {/* Timeframe Description */}
      <div className="bg-amber-50 border border-amber-200 rounded-lg p-3">
        <p className="text-sm text-amber-800">
          <strong>{TIMEFRAMES.find(tf => tf.value === activeTimeframe)?.label}:</strong>{' '}
          {TIMEFRAMES.find(tf => tf.value === activeTimeframe)?.description}
        </p>
      </div>

      {/* Outcomes List */}
      <div className="space-y-4">
        {getOutcomesByTimeframe(activeTimeframe).map((outcome) => (
          <div key={outcome.id} className="bg-white border border-gray-200 rounded-lg shadow-sm">
            {/* Outcome Header */}
            <div className="p-4 border-b border-gray-100">
              <div className="flex justify-between items-start">
                <div>
                  <h4 className="font-medium text-gray-900">{outcome.name}</h4>
                  {outcome.description && (
                    <p className="text-sm text-gray-600 mt-1">{outcome.description}</p>
                  )}
                </div>
                <div className="flex space-x-2">
                  <button
                    onClick={() => {
                      setOutcomeForm({ name: outcome.name, description: outcome.description || '' });
                      setEditingOutcome(outcome.id || null);
                    }}
                    className="text-gray-400 hover:text-blue-600"
                  >
                    <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                    </svg>
                  </button>
                  <button
                    onClick={() => outcome.id && handleDeleteOutcome(outcome.id)}
                    className="text-gray-400 hover:text-red-600"
                  >
                    <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                    </svg>
                  </button>
                </div>
              </div>
            </div>

            {/* Indicators */}
            <div className="p-4 bg-gray-50">
              <h5 className="text-sm font-medium text-gray-700 mb-3">
                📊 Indicators ({outcome.indicators?.length || 0})
              </h5>
              
              {outcome.indicators?.map((indicator) => (
                <div key={indicator.id} className="bg-white rounded p-3 mb-2 border border-gray-200">
                  <div className="flex justify-between items-start">
                    <div className="flex-1">
                      <div className="flex items-center gap-2">
                        <span className="font-medium text-sm">{indicator.name}</span>
                        <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                          indicator.status === 'achieved' ? 'bg-green-100 text-green-800' :
                          indicator.status === 'in_progress' ? 'bg-yellow-100 text-yellow-800' :
                          indicator.status === 'not_achieved' ? 'bg-red-100 text-red-800' :
                          'bg-gray-100 text-gray-600'
                        }`}>
                          {INDICATOR_STATUSES.find(s => s.value === indicator.status)?.label}
                        </span>
                      </div>
                      {indicator.measurement_method && (
                        <p className="text-xs text-gray-500 mt-1">📏 {indicator.measurement_method}</p>
                      )}
                      {renderProgressBar(indicator.baseline_value, indicator.target_value, indicator.actual_value)}
                    </div>
                    <div className="flex space-x-1 ml-2">
                      <button
                        onClick={() => {
                          setIndicatorForm({
                            name: indicator.name,
                            measurement_method: indicator.measurement_method || '',
                            baseline_value: indicator.baseline_value?.toString() || '',
                            target_value: indicator.target_value?.toString() || '',
                            actual_value: indicator.actual_value?.toString() || '',
                            target_date: indicator.target_date || '',
                            data_source: indicator.data_source || '',
                            status: indicator.status,
                          });
                          setEditingIndicator({ outcomeId: outcome.id!, indicatorId: indicator.id! });
                        }}
                        className="text-gray-400 hover:text-blue-600"
                      >
                        <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                        </svg>
                      </button>
                      <button
                        onClick={() => outcome.id && indicator.id && handleDeleteIndicator(outcome.id, indicator.id)}
                        className="text-gray-400 hover:text-red-600"
                      >
                        <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                        </svg>
                      </button>
                    </div>
                  </div>
                </div>
              ))}

              <button
                onClick={() => setEditingIndicator({ outcomeId: outcome.id!, indicatorId: null })}
                className="text-sm text-blue-600 hover:text-blue-800"
              >
                + Add Indicator
              </button>
            </div>
          </div>
        ))}

        {/* Add Outcome Button/Form */}
        {editingOutcome !== null ? (
          <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 space-y-3">
            <input
              type="text"
              value={outcomeForm.name}
              onChange={(e) => setOutcomeForm({ ...outcomeForm, name: e.target.value })}
              placeholder="Outcome name (e.g., 'Participants gain job-ready skills')"
              className="w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
            />
            <textarea
              value={outcomeForm.description}
              onChange={(e) => setOutcomeForm({ ...outcomeForm, description: e.target.value })}
              placeholder="Description (optional)"
              rows={2}
              className="w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
            />
            <div className="flex justify-end space-x-2">
              <button onClick={resetOutcomeForm} className="px-3 py-1.5 text-sm text-gray-600 hover:bg-gray-100 rounded">
                Cancel
              </button>
              <button
                onClick={handleSaveOutcome}
                disabled={saving || !outcomeForm.name.trim()}
                className="px-3 py-1.5 text-sm bg-blue-600 text-white rounded hover:bg-blue-700 disabled:opacity-50"
              >
                {saving ? 'Saving...' : 'Save Outcome'}
              </button>
            </div>
          </div>
        ) : (
          <button
            onClick={() => setEditingOutcome('')}
            className="w-full py-3 border-2 border-dashed border-gray-300 rounded-lg text-gray-600 hover:border-blue-400 hover:text-blue-600"
          >
            + Add Outcome
          </button>
        )}
      </div>

      {/* Indicator Form Modal */}
      {editingIndicator && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex items-center justify-center min-h-screen px-4">
            <div className="fixed inset-0 bg-gray-500 bg-opacity-75" onClick={resetIndicatorForm} />
            <div className="relative bg-white rounded-lg shadow-xl max-w-lg w-full p-6">
              <h3 className="text-lg font-medium mb-4">
                {editingIndicator.indicatorId ? 'Edit Indicator' : 'Add Indicator'}
              </h3>
              
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Indicator Name <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    value={indicatorForm.name}
                    onChange={(e) => setIndicatorForm({ ...indicatorForm, name: e.target.value })}
                    placeholder="e.g., % of participants reporting confidence increase"
                    className="w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Measurement Method
                  </label>
                  <input
                    type="text"
                    value={indicatorForm.measurement_method}
                    onChange={(e) => setIndicatorForm({ ...indicatorForm, measurement_method: e.target.value })}
                    placeholder="e.g., Pre/post survey, Administrative data"
                    className="w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                  />
                </div>

                <div className="grid grid-cols-3 gap-3">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Baseline</label>
                    <input
                      type="number"
                      value={indicatorForm.baseline_value}
                      onChange={(e) => setIndicatorForm({ ...indicatorForm, baseline_value: e.target.value })}
                      placeholder="0"
                      className="w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Target</label>
                    <input
                      type="number"
                      value={indicatorForm.target_value}
                      onChange={(e) => setIndicatorForm({ ...indicatorForm, target_value: e.target.value })}
                      placeholder="100"
                      className="w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Actual</label>
                    <input
                      type="number"
                      value={indicatorForm.actual_value}
                      onChange={(e) => setIndicatorForm({ ...indicatorForm, actual_value: e.target.value })}
                      placeholder="—"
                      className="w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Target Date</label>
                    <input
                      type="date"
                      value={indicatorForm.target_date}
                      onChange={(e) => setIndicatorForm({ ...indicatorForm, target_date: e.target.value })}
                      className="w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
                    <select
                      value={indicatorForm.status}
                      onChange={(e) => setIndicatorForm({ ...indicatorForm, status: e.target.value as any })}
                      className="w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                    >
                      {INDICATOR_STATUSES.map(s => (
                        <option key={s.value} value={s.value}>{s.label}</option>
                      ))}
                    </select>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Data Source</label>
                  <input
                    type="text"
                    value={indicatorForm.data_source}
                    onChange={(e) => setIndicatorForm({ ...indicatorForm, data_source: e.target.value })}
                    placeholder="e.g., Annual participant survey"
                    className="w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                  />
                </div>
              </div>

              <div className="mt-6 flex justify-end space-x-3">
                <button onClick={resetIndicatorForm} className="px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 rounded-md">
                  Cancel
                </button>
                <button
                  onClick={handleSaveIndicator}
                  disabled={saving || !indicatorForm.name.trim()}
                  className="px-4 py-2 text-sm bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50"
                >
                  {saving ? 'Saving...' : 'Save Indicator'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* SMART Guidance */}
      <div className="bg-gray-50 rounded-lg p-4">
        <h3 className="font-medium text-gray-900 mb-2">🎯 SMART Indicators</h3>
        <p className="text-sm text-gray-600">
          Good indicators are: <strong>S</strong>pecific, <strong>M</strong>easurable, <strong>A</strong>ction-oriented, <strong>R</strong>ealistic, <strong>T</strong>imed
        </p>
      </div>
    </div>
  );
}
